import { yupResolver } from '@hookform/resolvers/yup'
import { find, map } from 'lodash-es'
import { FC, useCallback, useEffect, useRef, useState } from 'react'
import { useFieldArray, useForm } from 'react-hook-form'
import * as yup from 'yup'

import { Group } from './Group'
import Loader from '../../../common/components/Loader'
import { useAppSelector } from '../../../store'
import { Currency } from '../../../common/models'
import {
  CurrencyAlignment,
  IGroup,
  PaymentProvider,
} from '../../../common/models/groups'
import { getGroups, getPaymentProviders } from '../../../core/services/groups'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui'
import { Icon } from '@/common/components/Icon'
import {
  loadApplePayConfig,
  loadGooglePayConfig,
} from './payment-providers/configLoaders'
import {
  applePayConfigSchema,
  googlePayConfigSchema,
} from './payment-providers/schema'
import {
  IApplePayConfigForm,
  IGooglePayConfigForm,
} from './payment-providers/types'

type Props = {
  currencies: Currency[]
}

export type TGroup = {
  id?: string
  name: string
  currencies?: TCurrency[]
  festivals?: TFestival[]
  saved?: boolean
}

export type TCurrency = {
  id?: string
  name: string
  symbol: string
  alignment: CurrencyAlignment
  active?: boolean
  main?: boolean
  conversionRate?: number
  saved?: boolean
}

export type FestivalCurrency = {
  id?: string
  name: string
  symbol?: string
  alignment: CurrencyAlignment
  active?: boolean
  main?: boolean
  conversionRate?: number
  saved?: boolean
}

export type PAYMENT_PROVIDER =
  | 'BT_IPAY'
  | 'STRIPE'
  | 'CRYPTO_PULSAR'
  | 'NOW_PAYMENTS'
  | 'EU_PLATESC'

export type TFestivalOrderTypes = 'TICKETS' | 'CHECKIN' | 'TRANSFER' | 'TOPUP'
export type TFestivalPaymentType =
  | 'CARD'
  | 'CRYPTO'
  | 'GOOGLE_PAY'
  | 'APPLE_PAY'

export type TFestivalPaymentProvider = {
  paymentProviderId: string
  orderTypes?: TFestivalOrderTypes[]
  saved?: boolean
  paymentType?: TFestivalPaymentType
  applePayConfig?: IApplePayConfigForm
  googlePayConfig?: IGooglePayConfigForm
}

export type TFestival = {
  id: string
  festivalPaymentProviderDtos?: TFestivalPaymentProvider[]
  apiSecret?: string
  username?: string
  password?: string
  currencies?: FestivalCurrency[]
  saved?: boolean
}

export type TFormValues = {
  groups?: TGroup[]
}

export const GroupsTab: FC<Props> = ({ currencies }): JSX.Element => {
  const [loading, setLoading] = useState(true)

  const [groups, setGroups] = useState<IGroup[]>([])
  const [paymentProviders, setPaymentProviders] = useState<PaymentProvider[]>(
    [],
  )

  // festivalId -> platformName, used to set the x-festival-name header when
  // fetching Apple/Google Pay configs (kept in a ref so the empty-deps getData
  // callback always reads the latest map).
  const { festivals } = useAppSelector((s) => s.app)
  const festivalPlatformsRef = useRef<Record<string, string>>({})
  useEffect(() => {
    festivalPlatformsRef.current = festivals.reduce<Record<string, string>>(
      (acc, festival) => {
        acc[festival.id] = festival.platformName
        return acc
      },
      {},
    )
  }, [festivals])

  const schema = yup.object().shape({
    groups: yup.array().of(
      yup.object().shape({
        id: yup.string().optional(),
        name: yup.string().required('Group name is required'),
        currencies: yup.array().of(
          yup.object().shape({
            id: yup.string().optional(),
            name: yup.string().required('Currency name is required'),
            symbol: yup.string().required('Currency symbol is required'),
            alignment: yup
              .string()
              .oneOf(['BEFORE_PRICE', 'AFTER_PRICE'])
              .required('Currency alignment is required'),
            active: yup.boolean().optional(),
            main: yup.boolean().optional(),
            conversionRate: yup
              .number()
              .optional()
              .transform((value) => {
                return isNaN(value) ? undefined : value
              }),
            saved: yup.boolean().optional(),
          }),
        ),
        festivals: yup.array().of(
          yup.object().shape({
            id: yup.string().required('Festival id is required'),
            currencies: yup.array().of(
              yup.object().shape({
                id: yup.string().required('Currency id is required'),
                name: yup.string().required('Currency name is required'),
                symbol: yup.string().optional(),
                alignment: yup
                  .string()
                  .oneOf(['BEFORE_PRICE', 'AFTER_PRICE'])
                  .required('Currency alignment is required'),
                active: yup.boolean().optional(),
                main: yup.boolean().optional(),
              }),
            ),
            festivalPaymentProviderDtos: yup.array().of(
              yup.object().shape({
                paymentProviderId: yup
                  .string()
                  .required('Payment provider id is required'),
                orderTypes: yup
                  .array()
                  .transform((value) =>
                    Array.isArray(value) ? value.filter(Boolean) : value,
                  )
                  .of(
                    yup
                      .string()
                      .oneOf(['TICKETS', 'CHECKIN', 'TRANSFER', 'TOPUP'])
                      .required('Order type is required'),
                  ),
                paymentType: yup
                  .string()
                  .oneOf(['CARD', 'CRYPTO', 'GOOGLE_PAY', 'APPLE_PAY'])
                  .required('Payment type is required'),
                applePayConfig: yup
                  .object()
                  .optional()
                  .default(undefined)
                  .when('paymentType', {
                    is: (val: string) => val === 'APPLE_PAY',
                    then: () => applePayConfigSchema.required(),
                    otherwise: (s) => s.optional().default(undefined),
                  }),
                googlePayConfig: yup
                  .object()
                  .optional()
                  .default(undefined)
                  .when('paymentType', {
                    is: (val: string) => val === 'GOOGLE_PAY',
                    then: () => googlePayConfigSchema.required(),
                    otherwise: (s) => s.optional().default(undefined),
                  }),
              }),
            ),
            saved: yup.boolean().optional(),
          }),
        ),
        saved: yup.boolean().optional(),
      }),
    ),
  })

  const getData = useCallback(async () => {
    setLoading(true)
    try {
      const [groupsResponseRes, paymentProvidersRes] = await Promise.all([
        getGroups(),
        getPaymentProviders(),
      ])
      if (
        groupsResponseRes.status === 200 &&
        paymentProvidersRes.status === 200
      ) {
        const { data: paymentProviders } = paymentProvidersRes
        setPaymentProviders(paymentProviders)
        const { data } = groupsResponseRes
        setGroups(data)
        for (const group of data) {
          const groupCurrencies = map(group.currencies, (currency) => {
            return {
              id: currency.id,
              name: currency.name,
              symbol: currency.symbol || '',
              alignment: currency.symbolAlignment,
              active: find(group.currenciesDetails, { id: currency.id })
                ?.active,
              main: find(group.currenciesDetails, { id: currency.id })?.default,
              conversionRate: find(group.currenciesDetails, { id: currency.id })
                ?.exchangeRate,
              saved: true,
            }
          })
          const groupFestivals = await Promise.all(
            map(group.festivals, async (festival) => {
              const festivalName =
                festivalPlatformsRef.current[festival.festivalId]
              const festivalPaymentProviderDtos = await Promise.all(
                map(festival.festivalPaymentProviderDtos, async (dto) => ({
                  ...dto,
                  applePayConfig:
                    dto.paymentType === 'APPLE_PAY'
                      ? await loadApplePayConfig(
                          dto.paymentProviderId,
                          festivalName,
                        )
                      : undefined,
                  googlePayConfig:
                    dto.paymentType === 'GOOGLE_PAY'
                      ? await loadGooglePayConfig(
                          dto.paymentProviderId,
                          festivalName,
                        )
                      : undefined,
                })),
              )
              return {
                id: festival.festivalId,
                paymentProvider: festival.paymentProviderName,
                apiSecret: festival.paymentProviderSecret,
                username: festival.paymentProviderUsername,
                password: festival.paymentProviderPassword,
                festivalPaymentProviderDtos,
                currencies: map(festival.currencies, (currency) => {
                  return {
                    id: currency.id,
                    name: currency.name,
                    symbol: '',
                    alignment: 'BEFORE_PRICE' as CurrencyAlignment,
                    active: currency.active,
                    default: currency.default,
                    main: currency.default,
                  }
                }),
                saved: true,
              }
            }),
          )
          append({
            name: group.name,
            id: group.id,
            currencies: groupCurrencies,
            festivals: groupFestivals,
            saved: true,
          })
        }
        append({
          name: '',
          currencies: [],
          festivals: [],
        })
        remove(data.length)
      }
    } catch (err) {
      console.log(err)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    getData()
  }, [getData])

  const {
    handleSubmit,
    register,
    control,
    watch,
    setValue,
    trigger,
    formState: { errors },
  } = useForm<TFormValues>({
    mode: 'onChange',
    // Conditional yup.object().when(...) configs infer as `{}`, which doesn't
    // match TFormValues — cast to bypass the resolver type mismatch.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    resolver: yupResolver<TFormValues>(schema as any),
  })

  const { fields, append, remove } = useFieldArray({
    control: control,
    name: 'groups',
  })

  const submit = (data: TFormValues) => {
    console.log(data)
  }

  return (
    <>
      <Card className="p-7.5 bg-gradient-4 border border-monochrome-120 space-y-4">
        {loading && <Loader />}
        <form id="groups-form" onSubmit={handleSubmit(submit)}>
          {map(fields, (field, index) => (
            <Group
              errors={errors}
              register={register}
              control={control}
              group={field}
              key={field.id}
              currencies={currencies}
              index={index}
              remove={remove}
              watch={watch}
              setValue={setValue}
              trigger={trigger}
              groups={groups}
              paymentProviders={paymentProviders}
            />
          ))}
        </form>
      </Card>
      <div className="w-full flex items-center justify-center mt-4">
        <Button
          variant="secondary"
          type="button"
          onClick={() => {
            append({
              name: '',
              currencies: [],
            })
          }}
        >
          Add new Group
          <Icon
            icon="plus"
            width="18"
            height="18"
            viewBox="0 0 18 18"
            fill="none"
          />
        </Button>
      </div>
    </>
  )
}
